/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';

import * as styles from './styles';
import { Button } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import { v4 as uuidv4 } from 'uuid';

import type { UUID } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { CaptionError, CaptionFocus, DefaultInput, DescriptionListMedium, NumberInput, PlaceholderText } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import { LIMIT_NUMBER_RECURRENCES } from '../constants';

interface RecurrenceItem {
  id: UUID;
  valueInKm?: number;
  name?: string;
  error?: string;
}

export interface RecurrenceItems {
  [id: UUID]: RecurrenceItem;
}

export interface RecurrenceInputsProps {
  items: RecurrenceItems;
  onItemsChange: (newItems: RecurrenceItems) => void;
  isDisabled?: boolean;
  className?: string;
}

export const RecurrenceInputs: React.FC<RecurrenceInputsProps> = ({ items, onItemsChange, isDisabled, className }) => {
  const [itemIdInFocus, setItemIdInFocus] = React.useState<string | undefined>(undefined);
  const hasReachedMaxLimit = Object.keys(items).length === LIMIT_NUMBER_RECURRENCES;

  const createNewRecurrenceItem = (): RecurrenceItem => ({
    id: uuidv4(),
    valueInKm: undefined,
    name: '',
  });

  const handleRecurrenceAdd = () => {
    Analytics.track('Maintenance New Recurrence Item Button Clicked');
    const newRecurrenceItem = createNewRecurrenceItem();
    onItemsChange({
      ...items,
      [newRecurrenceItem.id]: { ...newRecurrenceItem },
    });
  };

  const handleRecurrenceDelete = (deleteItem: RecurrenceItem) => {
    Analytics.track('Maintenance Delete Recurrence Item Button Clicked');

    const { [deleteItem.id]: _, ...otherRecurrenceItems } = items;
    onItemsChange({ ...otherRecurrenceItems });
  };

  const handleRecurrenceValueChange = (item: RecurrenceItem, newValue?: number) => {
    const newItem = { ...item, valueInKm: newValue, error: undefined };

    if (!newItem.valueInKm) {
      onItemsChange({
        ...items,
        [item.id]: {
          ...newItem,
          error: 'Insira um valor maior do que zero',
        },
      });
    } else if (isDuplicated(newItem)) {
      onItemsChange({
        ...items,
        [item.id]: {
          ...newItem,
          error: 'Insira valor não duplicado ou exclua',
        },
      });
    } else {
      onItemsChange({
        ...items,
        [item.id]: { ...newItem, error: undefined },
      });
    }
  };

  const isDuplicated = (item: RecurrenceItem) => {
    return Object.keys(items).some((itemId) => itemId !== item.id && items[itemId].valueInKm === item.valueInKm);
  };

  const handleRecurrenceNameChange = (item: RecurrenceItem, name?: string) => {
    if (!name) {
      onItemsChange({ ...items, [item.id]: { ...item, name: '' } });
    } else if (name.length <= 60) {
      onItemsChange({ ...items, [item.id]: { ...item, name } });
    }
  };

  const countdownMessage = (itemName: string) => {
    const remainingLetters = 60 - itemName.length;
    const isSingle = remainingLetters === 0 || remainingLetters === 1;

    return (
      <>
        {isSingle ? 'Falta ' : 'Faltam '}
        <strong>{remainingLetters}</strong>
        {isSingle ? ' caractere' : ' caracteres'}
      </>
    );
  };

  const renderItem = (item: RecurrenceItem, idx: number) => (
    <div key={idx} className={styles.itemWrapper}>
      <div className={styles.recurrenceContainer}>
        {idx === 0 && <DescriptionListMedium id='value'>Recorrência (km)</DescriptionListMedium>}
        <DescriptionListMedium className={styles.counterIndex}>
          <NumberInput
            placeholder='km'
            className={styles.recurrence}
            value={item.valueInKm}
            disabled={isDisabled}
            error={Boolean(item.error)}
            onNumberChange={(value) => handleRecurrenceValueChange(item, value)}
          />
        </DescriptionListMedium>
        <div className={styles.helperBlock}>
          <CaptionError id='value'>{item.error ? item.error : ''}</CaptionError>
        </div>
      </div>

      <div className={styles.itemWrapper}>
        <div className={styles.recurrenceContainer}>
          {idx === 0 && <DescriptionListMedium id='name'>O que precisa ser feito? (opcional)</DescriptionListMedium>}

          <div className={styles.itemWrapper}>
            <DefaultInput
              placeholder='Ex.: Fazer revisão ou Troca de óleo'
              className={styles.recurrenceName}
              value={item.name}
              disabled={isDisabled}
              onFocus={() => setItemIdInFocus(item.id)}
              onBlur={() => setItemIdInFocus(undefined)}
              onChange={(event) => handleRecurrenceNameChange(item, event.target.value)}
            />

            {Object.keys(items).length !== 1 && (
              <Button
                variant='text'
                color='secondary'
                icon={<Icon name='trash-2' />}
                className={styles.deleteButton(isDisabled)}
                onClick={() => {
                  handleRecurrenceDelete(item);
                }}
              />
            )}
          </div>
          <div className={styles.helperBlock}>
            <CaptionFocus>{itemIdInFocus === item.id ? countdownMessage(item.name || '') : ''}</CaptionFocus>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn(styles.wrapper, className)}>
      {Object.values(items).map(renderItem)}

      <Button variant='text' size='condensed' startIcon={<Icon name='plus' />} disabled={hasReachedMaxLimit || isDisabled} onClick={isDisabled ? undefined : handleRecurrenceAdd}>
        Adicionar lembrete
      </Button>

      {hasReachedMaxLimit && <PlaceholderText className={styles.limitMessage}>Você alcançou a quantidade máxima de lembretes de manutenção.</PlaceholderText>}
    </div>
  );
};
